import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sanitize admin-authored header/footer HTML before rendering to defend against XSS
// (settings are edited by super_admins, but we belt-and-braces sanitize anyway).
const sanitizeHtml = (dirty) => ({ __html: DOMPurify.sanitize(String(dirty || ''), { USE_PROFILES: { html: true } }) });

/**
 * A4-optimized printable participants report.
 * URL params: groupBy=group|room, sortField=..., sortDir=asc|desc
 * Each distinct group/room renders on its own printed page (page-break-after: always).
 */
const ActivityPrintReport = () => {
    const { activityId } = useParams();
    const [sp] = useSearchParams();
    const groupBy = sp.get('groupBy') || 'group';
    const sortField = sp.get('sortField') || 'student_name';
    const sortDir = sp.get('sortDir') || 'asc';

    const [loading, setLoading] = useState(true);
    const [activity, setActivity] = useState(null);
    const [participants, setParticipants] = useState([]);
    const [settings, setSettings] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const [act, parts, sett] = await Promise.all([
                    axios.get(`${API_URL}/api/activities/${activityId}`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/activities/${activityId}/participants`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/settings`, { withCredentials: true }),
                ]);
                setActivity(act.data);
                setParticipants(parts.data || []);
                setSettings(sett.data || {});
            } catch (err) {
                if (process.env.NODE_ENV === 'development') console.error('Failed to load report:', err);
            } finally { setLoading(false); }
        })();
    }, [activityId]);

    // Auto-open the print dialog once data is rendered (unless the URL disables it with ?preview=1)
    useEffect(() => {
        if (loading) return;
        if (sp.get('preview') === '1') return; // Preview mode — skip auto-print
        const t = setTimeout(() => window.print(), 400);
        return () => clearTimeout(t);
    }, [loading, participants.length, sp]);

    const grouped = useMemo(() => {
        const dir = sortDir === 'asc' ? 1 : -1;
        const sortRows = (rows) => [...rows].sort((a, b) => {
            const va = (a[sortField] || '').toString().toLowerCase();
            const vb = (b[sortField] || '').toString().toLowerCase();
            if (va < vb) return -1 * dir;
            if (va > vb) return 1 * dir;
            return 0;
        });
        // Filter out cancelled/refunded so the printed roster only shows active/paid
        const active = participants.filter(p => !['cancelled', 'refunded'].includes(p.status));
        const map = new Map();
        for (const p of active) {
            const key = (p[groupBy] || '').trim() || `— No ${groupBy} assigned —`;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(p);
        }
        // Sort group keys alphabetically, but push the "no assignment" bucket to the end
        return Array.from(map.entries())
            .sort(([a], [b]) => {
                if (a.startsWith('—')) return 1;
                if (b.startsWith('—')) return -1;
                return a.localeCompare(b);
            })
            .map(([name, rows]) => [name, sortRows(rows)]);
    }, [participants, groupBy, sortField, sortDir]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" />
            </div>
        );
    }
    if (!activity) return <div className="p-8">Activity not found</div>;

    const logoUrl = settings.logo_url || `${API_URL}/api/public/logo`;
    const headerHtml = settings.report_header || `<h1 style="margin:0;font-size:22px;color:#064E3B;">${settings.school_name || 'Nurulhuda Quranic School'}</h1>`;
    const footerHtml = settings.report_footer || `<p style="margin:0;">info@nhq.ie</p>`;

    return (
        <>
            <style>{`
                @page { size: A4 portrait; margin: 12mm 10mm; }
                @media print {
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    .no-print { display: none !important; }
                }
                body { background: #fff; margin: 0; }
                .report-body { font-family: Arial, sans-serif; color: #1F2937; max-width: 190mm; margin: 0 auto; padding: 8mm 0; }
                .page-section { page-break-after: always; break-after: page; }
                .page-section:last-of-type { page-break-after: auto; break-after: auto; }
                .r-header { display: flex; align-items: center; gap: 16px; border-bottom: 2px solid #064E3B; padding-bottom: 8px; margin-bottom: 12px; }
                .r-header img { max-height: 60px; max-width: 120px; }
                .r-title { font-size: 16px; font-weight: 600; color: #064E3B; margin: 12px 0 4px 0; }
                .r-meta { font-size: 11px; color: #4B5563; margin-bottom: 8px; }
                table.roster { width: 100%; border-collapse: collapse; font-size: 11px; }
                table.roster th, table.roster td { border: 1px solid #D1D5DB; padding: 5px 7px; text-align: left; vertical-align: top; }
                table.roster th { background: #F3F4F6; color: #1F2937; font-weight: 700; }
                table.roster tr:nth-child(even) td { background: #FAFAFA; }
                .r-footer { margin-top: 10px; padding-top: 6px; border-top: 1px solid #E5E7EB; font-size: 10px; color: #6B7280; text-align: center; }
                .toolbar { position: sticky; top: 0; background: #064E3B; color: #fff; padding: 10px 14px; display: flex; justify-content: space-between; align-items: center; font-family: Arial, sans-serif; font-size: 13px; z-index: 10; }
                .toolbar button { background: #ffffff; color: #064E3B; border: 0; padding: 6px 14px; border-radius: 4px; cursor: pointer; font-weight: 600; font-size: 12px; }
            `}</style>

            <div className="toolbar no-print">
                <span>Print preview — {activity.name} — grouped by <strong>{groupBy}</strong></span>
                <div>
                    <button onClick={() => window.print()} data-testid="print-again-btn">Print</button>
                </div>
            </div>

            <div className="report-body" data-testid="print-report-body">
                {grouped.length === 0 ? (
                    <div className="page-section">
                        <ReportHeader logoUrl={logoUrl} headerHtml={headerHtml} activity={activity} groupBy={groupBy} groupName="—" count={0} />
                        <p style={{ padding: '40px 0', textAlign: 'center', color: '#6B7280' }}>No participants to print.</p>
                        <div className="r-footer" dangerouslySetInnerHTML={sanitizeHtml(footerHtml)} />
                    </div>
                ) : (
                    grouped.map(([name, rows]) => (
                        <section key={name} className="page-section" data-testid={`print-section-${name}`}>
                            <ReportHeader logoUrl={logoUrl} headerHtml={headerHtml} activity={activity} groupBy={groupBy} groupName={name} count={rows.length} />
                            <table className="roster">
                                <thead>
                                    <tr>
                                        <th style={{ width: '5%' }}>#</th>
                                        <th style={{ width: '25%' }}>Student</th>
                                        <th style={{ width: '10%' }}>DOB</th>
                                        <th style={{ width: '20%' }}>Guardian</th>
                                        <th style={{ width: '15%' }}>Mobile</th>
                                        <th style={{ width: '10%' }}>{groupBy === 'group' ? 'Room' : 'Group'}</th>
                                        <th style={{ width: '15%' }}>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((p, i) => (
                                        <tr key={p.id}>
                                            <td>{i + 1}</td>
                                            <td>
                                                <div style={{ fontWeight: 600 }}>{p.student_name}</div>
                                                <div style={{ fontSize: 10, color: '#6B7280' }}>{p.student_reg_code}</div>
                                            </td>
                                            <td>{p.student_dob || '—'}</td>
                                            <td>{p.parent_name}</td>
                                            <td>{p.parent_mobile || '—'}</td>
                                            <td>{p[groupBy === 'group' ? 'room' : 'group'] || '—'}</td>
                                            <td></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="r-footer" dangerouslySetInnerHTML={sanitizeHtml(footerHtml)} />
                        </section>
                    ))
                )}
            </div>
        </>
    );
};

const ReportHeader = ({ logoUrl, headerHtml, activity, groupBy, groupName, count }) => (
    <>
        <div className="r-header">
            {logoUrl ? <img src={logoUrl} alt="Logo" onError={(e) => { e.target.style.display = 'none'; }} /> : null}
            <div style={{ flex: 1 }} dangerouslySetInnerHTML={sanitizeHtml(headerHtml)} />
        </div>
        <p className="r-title">{activity.name} — {groupBy === 'group' ? 'Group' : 'Room'}: {groupName}</p>
        <p className="r-meta">
            Dates: {activity.start_date} → {activity.end_date}
            {activity.location ? ` · Location: ${activity.location}` : ''}
            {' · '}Participants: <strong>{count}</strong>
            {' · '}Printed: {new Date().toLocaleString('en-IE')}
        </p>
    </>
);

export default ActivityPrintReport;
